import { getMapStyles } from './map.js';
import { sources } from '../sources.js';
import { layers } from '../layers/index.js';
import { whenMapStyleReady } from './mapGuards';

// Debug flag to control console.log messages
const DEBUG = false;

/**
 * Background Layer Manager
 * 
 * Provides a centralized system for managing background layers.
 * Handles adding layers if they don't exist, showing/hiding them,
 * and updating the Redux state.
 */

// Available background layers
const BACKGROUND_LAYERS = {
  'base': {
    name: 'Base Map',
    description: 'Standard street map',
    layerId: null, // No additional layer needed for base
    sourceId: null
  },
  'satellite': {
    name: 'Satellite',
    description: 'Satellite imagery',
    layerId: 'luchtfoto-pdok',
    sourceId: 'luchtfoto-pdok'
  }
};

/**
 * Get all available background layer options
 */
export const getAvailableBackgroundLayers = () => {
  return BACKGROUND_LAYERS;
};

/**
 * Check if a background layer exists on the map
 */
const layerExists = (map, layerId) => {
  if (!layerId) return true; // Base layer always exists
  return map.getLayer(layerId) !== undefined;
};

/**
 * Check if there are multiple layers with the same ID
 */
const checkForDuplicateLayers = (map, layerId) => {
  if (!layerId) return;
  
  const style = map.getStyle();
  const layersWithSameId = style.layers.filter(layer => layer.id === layerId);
  
  if (layersWithSameId.length > 1) {
    console.warn(`Found ${layersWithSameId.length} layers with ID: ${layerId}`);
    if (DEBUG) {
      layersWithSameId.forEach((layer, index) => {
        console.log(`Layer ${index + 1}:`, layer);
      });
    }
  }
};

/**
 * Check if a source exists on the map
 */
const sourceExists = (map, sourceId) => {
  if (!sourceId) return true; // Base layer doesn't need a source
  return map.getSource(sourceId) !== undefined;
};

/**
 * Add a background layer and its source to the map
 */
const addBackgroundLayerToMap = (map, layerName) => {
  const layerConfig = BACKGROUND_LAYERS[layerName];
  if (!layerConfig) {
    console.warn(`Unknown background layer: ${layerName}`);
    return false;
  }

  // Base layer doesn't need additional layers/sources
  if (layerName === 'base') {
    return true;
  }

  const { layerId, sourceId } = layerConfig;

  // Add source if it doesn't exist
  if (sourceId && !sourceExists(map, sourceId)) {
    const sourceConfig = sources[sourceId];
    if (sourceConfig) {
      map.addSource(sourceId, sourceConfig);
      if (DEBUG) console.log(`Added source: ${sourceId}`);
    } else {
      console.warn(`Source config not found for: ${sourceId}`);
      return false;
    }
  }

  // Add layer if it doesn't exist
  if (layerId && !layerExists(map, layerId)) {
    const layerConfig = layers[layerId];
    if (layerConfig) {
      // Ensure the layer has a layout property with visibility
      const layerWithLayout = {
        ...layerConfig,
        layout: {
          ...layerConfig.layout,
          visibility: 'none' // Start hidden
        }
      };
      
      // For background layers, we need to add them at the correct position
      if (layerConfig['is-background-layer'] === true) {
        // Find the correct insertion point for background layers
        const style = map.getStyle();
        let beforeId = null;
        for (let i = 0; i < style.layers.length; i++) {
          const layer = style.layers[i];
          // Insert after basic map layers like 'land', 'water', 'building', etc.
          // but before labels and other overlay layers
          if (layer.id === 'building' || layer.id === 'road-simple' || layer.id === 'bridge-simple') {
            beforeId = style.layers[i + 1]?.id || null;
            break;
          }
        }
        
        // Add layer with beforeId to position it correctly
        map.addLayer(layerWithLayout, beforeId);
        if (DEBUG) console.log(`Added background layer: ${layerId} before: ${beforeId}`);
      } else {
        // For non-background layers, add at the end (on top)
        map.addLayer(layerWithLayout);
        if (DEBUG) console.log(`Added layer: ${layerId} with visibility: none`);
      }
    } else {
      console.warn(`Layer config not found for: ${layerId}`);
      return false;
    }
  }

  return true;
};

/**
 * Force remove and re-add a layer to ensure proper visibility control
 */
const forceReaddLayer = (map, layerId) => {
  try {
    const layerConfig = layers[layerId];
    if (layerConfig) {
      // Remove the layer
      map.removeLayer(layerId);
      if (DEBUG) console.log(`Removed layer: ${layerId}`);
      
      // Re-add with proper layout
      const layerWithLayout = {
        ...layerConfig,
        layout: {
          ...layerConfig.layout,
          visibility: 'none' // Start hidden
        }
      };
      
      // For background layers, we need to add them at the correct position
      if (layerConfig['is-background-layer'] === true) {
        // Find the correct insertion point for background layers
        const style = map.getStyle();
        let beforeId = null;
        for (let i = 0; i < style.layers.length; i++) {
          const layer = style.layers[i];
          // Insert after basic map layers like 'land', 'water', 'building', etc.
          // but before labels and other overlay layers
          if (layer.id === 'building' || layer.id === 'road-simple' || layer.id === 'bridge-simple') {
            beforeId = style.layers[i + 1]?.id || null;
            break;
          }
        }
        
        // Add layer with beforeId to position it correctly
        map.addLayer(layerWithLayout, beforeId);
        if (DEBUG) console.log(`Re-added background layer: ${layerId} before: ${beforeId}`);
      } else {
        // For non-background layers, add at the end (on top)
        map.addLayer(layerWithLayout);
        if (DEBUG) console.log(`Re-added layer: ${layerId} with visibility: none`);
      }
      return true;
    }
  } catch (e) {
    console.warn(`Failed to force re-add layer ${layerId}:`, e);
  }
  return false;
};

/**
 * Show a specific background layer and hide others
 */
const showBackgroundLayer = (map, layerName) => {
  const layerConfig = BACKGROUND_LAYERS[layerName];
  if (!layerConfig) {
    console.warn(`Unknown background layer: ${layerName}`);
    return false;
  }

  if (DEBUG) console.log(`Setting background layer to: ${layerName}`);

  // Check for duplicate layers
  Object.keys(BACKGROUND_LAYERS).forEach(name => {
    const config = BACKGROUND_LAYERS[name];
    if (config.layerId) {
      checkForDuplicateLayers(map, config.layerId);
    }
  });

  // Hide all background layers first
  Object.keys(BACKGROUND_LAYERS).forEach(name => {
    const config = BACKGROUND_LAYERS[name];
    if (config.layerId && layerExists(map, config.layerId)) {
      if (DEBUG) console.log(`Hiding layer: ${config.layerId}`);
      
      // Try multiple methods to ensure the layer is hidden
      try {
        map.U.hide(config.layerId);
        if (DEBUG) console.log(`Used map.U.hide for ${config.layerId}`);
      } catch (e) {
        console.warn(`map.U.hide failed for ${config.layerId}:`, e);
      }
      
      // Also set visibility to 'none' as a backup
      try {
        map.setLayoutProperty(config.layerId, 'visibility', 'none');
        if (DEBUG) console.log(`Set visibility to 'none' for ${config.layerId}`);
      } catch (e) {
        console.warn(`Could not set visibility for ${config.layerId}:`, e);
      }
      
      // Check if layer is actually hidden
      try {
        const layer = map.getLayer(config.layerId);
        if (layer && layer.layout && layer.layout.visibility) {
          if (DEBUG) console.log(`Layer ${config.layerId} visibility: ${layer.layout.visibility}`);
        } else {
          if (DEBUG) console.log(`Layer ${config.layerId} - no visibility property found, forcing re-add`);
          // Force re-add the layer with proper visibility control
          forceReaddLayer(map, config.layerId);
        }
      } catch (e) {
        console.warn(`Could not check layer visibility for ${config.layerId}:`, e);
      }
      
      // Force a map repaint to ensure changes are applied
      try {
        map.triggerRepaint();
        if (DEBUG) console.log(`Triggered repaint after hiding ${config.layerId}`);
      } catch (e) {
        console.warn(`Could not trigger repaint:`, e);
      }
    }
  });

  // Show the requested layer
  if (layerName === 'base') {
    // For base layer, we just hide the satellite layer (already done above)
    if (DEBUG) console.log('Base layer selected - satellite should be hidden');
  } else {
    // Show the specific background layer
    const { layerId } = layerConfig;
    if (layerId && layerExists(map, layerId)) {
      if (DEBUG) console.log(`Showing layer: ${layerId}`);
      
      // Try multiple methods to ensure the layer is shown
      try {
        map.U.show(layerId);
        if (DEBUG) console.log(`Used map.U.show for ${layerId}`);
      } catch (e) {
        console.warn(`map.U.show failed for ${layerId}:`, e);
      }
      
      // Also set visibility to 'visible' as a backup
      try {
        map.setLayoutProperty(layerId, 'visibility', 'visible');
        if (DEBUG) console.log(`Set visibility to 'visible' for ${layerId}`);
      } catch (e) {
        console.warn(`Could not set visibility for ${layerId}:`, e);
      }
      
      // Check if layer is actually shown
      try {
        const layer = map.getLayer(layerId);
        if (layer && layer.layout && layer.layout.visibility) {
          if (DEBUG) console.log(`Layer ${layerId} visibility: ${layer.layout.visibility}`);
        }
      } catch (e) {
        console.warn(`Could not check layer visibility for ${layerId}:`, e);
      }
    }
  }

  return true;
};

/**
 * Set the background layer
 * 
 * @param {Object} map - The map instance
 * @param {string} layerName - Name of the background layer to set
 * @param {Function} onSuccess - Callback to execute after successful layer change
 * @param {Function} onError - Callback to execute if layer change fails
 */
export const setBackgroundLayer = (map, layerName, onSuccess = null, onError = null) => {
  // Validate input
  if (!map) {
    console.error('Map instance is required');
    if (onError) onError('Map instance is required');
    return;
  }

  if (!BACKGROUND_LAYERS[layerName]) {
    console.error(`Unknown background layer: ${layerName}`);
    if (onError) onError(`Unknown background layer: ${layerName}`);
    return;
  }

  // Check if map style is loaded
  if (!map.isStyleLoaded()) {
    if (DEBUG) console.log('Map style not loaded, waiting for style to load...');
    whenMapStyleReady(map, () => {
      setBackgroundLayer(map, layerName, onSuccess, onError);
    });
    return;
  }

  try {
    // Add the layer if it doesn't exist
    const layerAdded = addBackgroundLayerToMap(map, layerName);
    if (!layerAdded) {
      if (onError) onError(`Failed to add background layer: ${layerName}`);
      return;
    }

    // Show the layer
    const layerShown = showBackgroundLayer(map, layerName);
    if (!layerShown) {
      if (onError) onError(`Failed to show background layer: ${layerName}`);
      return;
    }

    // When satellite is active, hide streets at neighbourhood zoom to avoid obscuring aerial imagery
    updateStreetVisibilityForSatellite(map, layerName);

    if (DEBUG) console.log(`Background layer set to: ${layerName}`);
    
    // Execute success callback
    if (onSuccess) {
      onSuccess(layerName);
    }
  } catch (error) {
    console.error('Error setting background layer:', error);
    if (onError) onError(error.message);
  }
};

/**
 * Street layer IDs that render white/grey streets on the base map.
 * These are hidden when satellite is active and zoomed in to neighbourhood level (zoom >= 15)
 * to avoid obscuring the aerial imagery.
 */
const STREET_LAYER_IDS = [
  'road-simple',
  'bridge-case-simple',
  'bridge-simple',
  'tunnel-simple',
  'road-path',
  'road-steps',
  'road-pedestrian',
  'bridge-path',
  'bridge-steps',
  'bridge-pedestrian',
  'tunnel-path',
  'tunnel-steps',
  'tunnel-pedestrian'
];

/** Zoom level at which streets start fading out (satellite mode) */
const STREET_FADE_ZOOM_START = 14;

/** Zoom level at which streets are fully hidden (satellite mode) */
const STREET_FADE_ZOOM_END = 16;

/**
 * Update street layer visibility based on map style and zoom.
 * When satellite is active: streets fade out gradually between zoom 14–15 (during zoom).
 * Otherwise: streets are fully visible.
 * Uses line-opacity driven by zoom level for a smooth fade while zooming.
 *
 * @param {Object} map - The map instance
 * @param {string} mapStyle - Current map style ('base' or 'satellite')
 */
export const updateStreetVisibilityForSatellite = (map, mapStyle) => {
  if (!map || !map.isStyleLoaded()) return;

  const zoom = map.getZoom();

  let opacity;
  if (mapStyle !== 'satellite') {
    opacity = 1;
  } else if (zoom <= STREET_FADE_ZOOM_START) {
    opacity = 1;
  } else if (zoom >= STREET_FADE_ZOOM_END) {
    opacity = 0;
  } else {
    // Linear fade between STREET_FADE_ZOOM_START and STREET_FADE_ZOOM_END
    opacity = 1 - (zoom - STREET_FADE_ZOOM_START) / (STREET_FADE_ZOOM_END - STREET_FADE_ZOOM_START);
  }

  STREET_LAYER_IDS.forEach((layerId) => {
    try {
      if (map.getLayer(layerId)) {
        map.setPaintProperty(layerId, 'line-opacity', opacity);
      }
    } catch (e) {
      // Layer may not exist in current style or may not support line-opacity
    }
  });
};

/**
 * Get the currently active background layer
 */
export const getCurrentBackgroundLayer = (map) => {
  if (!map || !map.isStyleLoaded()) {
    return 'base'; // Default fallback
  }

  // Check if satellite layer is visible
  const satelliteConfig = BACKGROUND_LAYERS['satellite'];
  if (satelliteConfig.layerId && layerExists(map, satelliteConfig.layerId)) {
    // Check if the layer is visible (this is a simplified check)
    // In a real implementation, you might want to track the current layer in state
    const layer = map.getLayer(satelliteConfig.layerId);
    if (layer && layer.layout && layer.layout.visibility === 'visible') {
      return 'satellite';
    }
  }

  return 'base';
}; 